import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import toast from '@/plugins/toasts'
import errorHandler from '@/plugins/error-handler'
import { useTranslation } from '@/composables/useTranslation'

/**
 * Composable for pin/unpin/pin-edit item actions.
 * Shared between All.vue (bookmarks) and Collection.vue.
 *
 * @param {Object} options
 * @param {Function} options.refreshItems - View-specific refresh function
 * @param {Function} options.getPinPath - Returns the route path for the pin form given an item
 */
export function usePinActions({ refreshItems, getPinPath }) {
  const store = useStore()
  const router = useRouter()
  const { t } = useTranslation()

  const handlePin = async (item) => {
    const pinnedCount = store.getters['collectionItems/numberOfPinnedItems']
    const collectionId = item.ownerId || store.getters['collectionItems/collectionId']

    if (pinnedCount === 0) {
      try {
        await store.dispatch('collectionItem/pinItem', {
          collectionId,
          itemId: item.id,
          position: 1,
        })
        toast.info(t('common.item_pinned'))
        await refreshItems()
      } catch (err) {
        errorHandler.toast(err)
      }
    } else {
      const query = { pinnedCount, collectionId }
      if (item.pinPosition != null) query.currentPosition = item.pinPosition
      router.push({ path: getPinPath(item), query })
    }
  }

  const handleUnpin = async (item) => {
    const collectionId = item.ownerId || store.getters['collectionItems/collectionId']
    try {
      await store.dispatch('collectionItem/unpinItem', {
        collectionId,
        itemId: item.id,
      })
      toast.info(t('common.pin_removed'))
      await refreshItems()
    } catch (err) {
      errorHandler.toast(err)
    }
  }

  const handlePinEdit = (item) => {
    const pinnedCount = store.getters['collectionItems/numberOfPinnedItems']
    const collectionId = item.ownerId || store.getters['collectionItems/collectionId']
    router.push({
      path: getPinPath(item),
      query: { currentPosition: item.pinPosition, pinnedCount, collectionId },
    })
  }

  return { handlePin, handleUnpin, handlePinEdit }
}
